import { useEffect, useState } from "react";
import { CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import ListenButton from "../../components/ListenButton";
import GameShell, { shuffle } from "./GameShell";

export default function RhymeGame() {
    const [data, setData] = useState([]);
    const [idx, setIdx] = useState(0);
    const [picked, setPicked] = useState(null);
    const [correct, setCorrect] = useState(0);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get("/games/rhymes").then((r) => setData(shuffle(r.data)));
    }, []);

    const pick = (option) => {
        if (picked) return;
        setPicked(option);
        if (option === data[idx].rhymes_with) {
            setCorrect((c) => c + 1);
            toast.success("Yes — they rhyme!");
        } else {
            toast.error(`"${data[idx].word}" rhymes with "${data[idx].rhymes_with}"`);
        }
        setTimeout(() => {
            if (idx + 1 >= data.length) {
                setDone(true);
                api.post("/progress/game-score", { game: "rhyme", score: (correct + (option === data[idx].rhymes_with ? 1 : 0)) * 6, correct: correct + (option === data[idx].rhymes_with ? 1 : 0), total: data.length }).catch(() => {});
            } else {
                setIdx(idx + 1);
                setPicked(null);
            }
        }, 900);
    };

    const reset = () => { setIdx(0); setCorrect(0); setDone(false); setPicked(null); setData(shuffle(data)); };

    if (!data.length) return <GameShell title="Rhyme Match"><p>Loading…</p></GameShell>;

    const current = data[idx];
    const options = shuffle([current.rhymes_with, current.decoy]);

    return (
        <GameShell
            title="Rhyme Match"
            intro="Which word rhymes? Trust your ear."
            testId="rhyme-game"
            instructions="This is the Rhyme Match game. Listen to the target word at the top. Then tap the word from the three choices that rhymes with it."
        >
            <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-[#819888]">Round {idx + 1} of {data.length}</p>
                <p className="text-sm text-[#2D5D4E] font-medium" data-testid="rhyme-score">Correct: {correct}</p>
            </div>

            {done ? (
                <div className="card-rw p-10 text-center">
                    <CheckCircle2 className="w-12 h-12 text-[#3A7D5C] mx-auto mb-4" strokeWidth={1.6} />
                    <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>Round complete</h2>
                    <p className="text-lg text-[#515B57] mb-6">{correct} of {data.length} correct.</p>
                    <button onClick={reset} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium" data-testid="rhyme-replay">
                        <RotateCcw className="w-4 h-4" /> Play again
                    </button>
                </div>
            ) : (
                <>
                    <div className="card-rw p-10 text-center mb-6">
                        <p className="label-eyebrow mb-3">Which word rhymes with</p>
                        <p className="text-5xl font-semibold mb-5" style={{ fontFamily: "Outfit" }} data-testid="rhyme-target">{current.word}</p>
                        <ListenButton text={current.word} testId="rhyme-listen" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        {options.map((opt) => {
                            const isPicked = picked === opt;
                            const isCorrect = picked && opt === current.rhymes_with;
                            const cls = isCorrect ? "tile tile-correct" : isPicked ? "tile tile-wrong" : "tile";
                            return (
                                <button
                                    key={opt}
                                    onClick={() => pick(opt)}
                                    className={`${cls} text-3xl py-8`}
                                    data-testid={`rhyme-option-${opt}`}
                                    disabled={!!picked}
                                >
                                    {opt}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </GameShell>
    );
}
